import { apiFetch } from './supabase';

export const IFC_CHUNK_BYTES = 5 * 1024 * 1024;
const DEFAULT_ATTEMPTS = 3;

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const responsePayload = async (response) => {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
};

const isTransientStatus = (status) =>
  status === 408 || status === 425 || status === 429 || status >= 500;

export const uploadFileInChunks = async ({
  file,
  fileType,
  uploadId,
  projectId,
  apiUrl,
  request = apiFetch,
  onProgress = () => {},
  chunkBytes = IFC_CHUNK_BYTES,
  maxAttempts = DEFAULT_ATTEMPTS,
  retryWait = wait,
  pollIntervalMs = 2000,
  maxPolls = 300,
  maxFinalizationRetries = 2,
}) => {
  const totalChunks = Math.ceil(file.size / chunkBytes);

  const sendChunk = async (chunkIndex) => {
    const chunk = file.slice(
      chunkIndex * chunkBytes,
      Math.min((chunkIndex + 1) * chunkBytes, file.size),
    );
    let lastError;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const form = new FormData();
        form.append('file', chunk, file.name);
        form.append('upload_id', uploadId);
        form.append('project_id', projectId);
        form.append('chunk_index', chunkIndex.toString());
        form.append('total_chunks', totalChunks.toString());
        form.append('file_type', fileType);
        form.append('file_name', file.name);
        form.append('file_size', file.size.toString());
        const response = await request(`${apiUrl}/api/audits/upload-chunk`, {
          method: 'POST',
          body: form,
        });
        const payload = await responsePayload(response);
        if (!response.ok) {
          const error = new Error(payload.detail || `Chunk ${chunkIndex + 1} failed (${response.status})`);
          if (!isTransientStatus(response.status) || attempt === maxAttempts) throw error;
          lastError = error;
        } else {
          if (!['chunk_received', 'finalizing', 'finalization_failed', 'complete'].includes(payload.status)) {
            throw new Error(`Chunk ${chunkIndex + 1} was not acknowledged by the server`);
          }
          if (payload.total_chunks !== totalChunks) {
            throw new Error('Server upload state does not match this file');
          }
          if (payload.status === 'complete' && payload.file_ready !== true) {
            throw new Error('Server reported completion without a ready IFC');
          }
          onProgress({
            receivedChunks: payload.received_chunks,
            totalChunks,
            percent: payload.status === 'complete' && payload.file_ready === true
              ? 100
              : Math.min(99, Math.round((payload.received_chunks / totalChunks) * 100)),
            status: payload.status,
          });
          return payload;
        }
      } catch (error) {
        lastError = error;
        if (attempt === maxAttempts || (error?.message && !/fetch|network|timeout/i.test(error.message))) {
          throw error;
        }
      }
      await retryWait(250 * attempt);
    }
    throw lastError || new Error(`Chunk ${chunkIndex + 1} failed`);
  };

  let finalState = null;
  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
    finalState = await sendChunk(chunkIndex);
  }

  for (let attempt = 1;
    attempt < maxAttempts && finalState?.status === 'chunk_received';
    attempt += 1) {
    await retryWait(250 * attempt);
    finalState = await sendChunk(totalChunks - 1);
  }

  const statusUrl = `${apiUrl}/api/audits/chunk-upload-status?${new URLSearchParams({
    upload_id: uploadId,
    project_id: projectId,
    file_type: fileType,
    ...(fileType === 'ifc' ? {} : { file_name: file.name }),
  })}`;
  let finalizationRetries = 0;
  for (let poll = 0;
    poll < maxPolls && finalState?.status !== 'complete';
    poll += 1) {
    if (finalState?.status === 'finalization_failed') {
      if (finalizationRetries >= maxFinalizationRetries) {
        throw new Error(finalState.failure_reason || 'IFC finalization failed');
      }
      const retryForm = new FormData();
      retryForm.append('upload_id', uploadId);
      retryForm.append('project_id', projectId);
      retryForm.append('file_type', fileType);
      retryForm.append('file_name', file.name);
      const retryResponse = await request(`${apiUrl}/api/audits/retry-chunk-finalization`, {
        method: 'POST', body: retryForm,
      });
      const retryPayload = await responsePayload(retryResponse);
      if (!retryResponse.ok) {
        throw new Error(retryPayload.detail || 'Unable to retry IFC finalization');
      }
      finalState = retryPayload;
      finalizationRetries += 1;
    }
    onProgress({ receivedChunks: totalChunks, totalChunks, percent: 99, status: 'finalizing' });
    await retryWait(pollIntervalMs);
    let statusResponse;
    try {
      statusResponse = await request(statusUrl, { method: 'GET' });
    } catch (error) {
      if (poll < maxPolls - 1) continue;
      throw error;
    }
    const statusPayload = await responsePayload(statusResponse);
    if (!statusResponse.ok) {
      if (isTransientStatus(statusResponse.status) && poll < maxPolls - 1) continue;
      throw new Error(statusPayload.detail || 'Unable to check IFC finalization status');
    }
    finalState = statusPayload;
    if (finalState.status === 'complete' && finalState.file_ready === true) {
      onProgress({ receivedChunks: totalChunks, totalChunks, percent: 100, status: 'complete' });
    }
  }

  if (finalState?.status !== 'complete' || finalState?.file_ready !== true) {
    throw new Error(
      finalState?.failure_reason
        || 'Upload reached the browser, but the server did not confirm the IFC is ready',
    );
  }
  return finalState;
};
